import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth as useMentorAppAuth } from '../../../AuthContext';
import { useSelector } from 'react-redux';
import { baseURL } from '../../../aws-config';

const FlowstateAuthContext = createContext(undefined);

// Helper to check if token is expired
const isTokenExpired = () => {
    const expiration = localStorage.getItem('token_expiration');
    if (!expiration) return true;
    return Date.now() > parseInt(expiration, 10);
};

// Helper to clear all auth data
const clearAuthData = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_expiration');
    localStorage.removeItem('flowstate_user');
    localStorage.removeItem('flowstate_purchased_modules');
};

// Helper to redirect to login with return URL
const redirectToLogin = () => {
    const currentPath = window.location.pathname + window.location.search;
    clearAuthData();
    window.location.href = `/interview-prep/login?redirect=${encodeURIComponent(currentPath)}`;
};

/**
 * Extracts display initials from a user object
 * Priority: 1. Split Name, 2. Split Email, 3. Raw chars, 4. 'U' fallback
 */
export const getInitials = (user) => {
    if (!user) return 'U';
    
    // 1. Try to get from name if it's a real name and not just "User"
    if (user.name && user.name !== 'User' && user.name.trim() !== '') {
        const parts = user.name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return parts[0][0].toUpperCase();
    }

    // 2. Try to get from email
    if (user.email && user.email.includes('@')) {
        const namePart = user.email.split('@')[0];
        // Handle common email patterns: jane.doe, jane_doe, jane-doe
        const separators = /[\.\-_\s]+/;
        const parts = namePart.split(separators).filter(p => p.length > 0);
        
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        
        // If no separator, take first two letters of the name part (avoid taking numbers)
        const lettersOnly = namePart.replace(/[^a-zA-Z]/g, '');
        if (lettersOnly.length >= 2) {
            return lettersOnly.substring(0, 2).toUpperCase();
        }
        if (namePart.length >= 2) {
            return namePart.substring(0, 2).toUpperCase();
        }
        return namePart.charAt(0).toUpperCase();
    }

    // 3. Fallback to name's first char
    if (user.name && user.name.length > 0 && user.name !== 'User') {
        return user.name.charAt(0).toUpperCase();
    }

    return 'U';
};

export const FlowstateAuthProvider = ({ children }) => {
    const { googleResponse, updateGoogleResponse } = useMentorAppAuth();
    const [user, setUser] = useState(null);
    const [purchasedModules, setPurchasedModules] = useState([]);

    // Get user info from Redux store if available
    const reduxUser = useSelector(state => state.userProfile?.data);

    // Check token expiration on mount and periodically
    useEffect(() => {
        const checkTokenExpiration = () => {
            const accessToken = localStorage.getItem('access_token');
            if (accessToken && isTokenExpired()) {
                console.log('Token expired, clearing auth state');
                clearAuthData();
                setUser(null);
                setPurchasedModules([]);
                updateGoogleResponse(null);
            }
        };

        // Check immediately on mount
        checkTokenExpiration();

        // Check every minute
        const interval = setInterval(checkTokenExpiration, 60000);
        return () => clearInterval(interval);
    }, [updateGoogleResponse]);

    useEffect(() => {
        // Check if user is authenticated via mentor-app
        if (googleResponse?.access_token) {
            // First check if token is expired
            if (isTokenExpired()) {
                console.log('Token expired during auth check');
                clearAuthData();
                setUser(null);
                updateGoogleResponse(null);
                return;
            }

            // Try to get user details from localStorage or Redux
            const storedUser = localStorage.getItem('flowstate_user');
            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                } catch (e) {
                    console.error('Failed to parse stored user');
                }
            } else if (reduxUser) {
                // Create user object from Redux user
                const flowstateUser = {
                    id: reduxUser.id || reduxUser.user_id,
                    name: reduxUser.name || reduxUser.full_name || reduxUser.username || reduxUser.email?.split('@')[0] || 'User',
                    email: reduxUser.email || '',
                    purchasedModules: reduxUser.purchasedModules || []
                };
                setUser(flowstateUser);
                localStorage.setItem('flowstate_user', JSON.stringify(flowstateUser));
            } else {
                // Try to fallback from onboardingData in localStorage (common for Google login)
                const storedOnboardingData = localStorage.getItem('onboardingData');
                let foundOnboardingUser = null;
                if (storedOnboardingData) {
                    try {
                        const parsed = JSON.parse(storedOnboardingData);
                        foundOnboardingUser = {
                            id: 'user',
                            name: parsed.user?.name || parsed.full_name || parsed.name || parsed.email?.split('@')[0] || 'User',
                            email: parsed.user?.email || parsed.email || '',
                            purchasedModules: []
                        };
                    } catch (e) { }
                }

                // Try one more source: googleResponse itself if it contains user info (live login)
                let foundGoogleUser = null;
                if (googleResponse?.user || googleResponse?.email) {
                    foundGoogleUser = {
                        id: 'google-user',
                        name: googleResponse.user?.name || googleResponse.full_name || googleResponse.email?.split('@')[0] || 'User',
                        email: googleResponse.user?.email || googleResponse.email || '',
                        purchasedModules: []
                    };
                }

                const bestAvailableUser = foundGoogleUser || foundOnboardingUser;

                if (bestAvailableUser) {
                    setUser(bestAvailableUser);
                    // If we found a good email, persist it so it stays across refreshes
                    if (bestAvailableUser.email) {
                        localStorage.setItem('flowstate_user', JSON.stringify(bestAvailableUser));
                    }
                } else {
                    // Final fallback: minimal user if no info found
                    setUser({
                        id: 'user',
                        name: 'User',
                        email: '',
                        purchasedModules: []
                    });
                }
            }
        } else {
            setUser(null);
        }
    }, [googleResponse, reduxUser, updateGoogleResponse]);

    // Load purchased modules from backend API when authenticated
    useEffect(() => {
        const fetchPurchasedModules = async () => {
            const accessToken = localStorage.getItem('access_token');
            if (!accessToken) return;

            // Check token expiration before API call
            if (isTokenExpired()) {
                console.log('Token expired before fetching modules');
                clearAuthData();
                setUser(null);
                setPurchasedModules([]);
                updateGoogleResponse(null);
                return;
            }

            try {
                const response = await fetch(`${baseURL}api/interview-prep/access/`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    // Combine purchased categories and all categories from purchased tracks
                    const modules = [
                        ...(data.purchased_categories || []),
                        ...(data.purchased_tracks || [])
                    ];
                    setPurchasedModules(modules);
                    localStorage.setItem('flowstate_purchased_modules', JSON.stringify(modules));
                } else if (response.status === 401) {
                    // Token is expired or invalid - clear auth state
                    console.log('401 received, token invalid');
                    clearAuthData();
                    setUser(null);
                    setPurchasedModules([]);
                    updateGoogleResponse(null);
                }
            } catch (e) {
                console.error('Failed to fetch purchased modules:', e);
                // Fallback to localStorage
                const stored = localStorage.getItem('flowstate_purchased_modules');
                if (stored) {
                    try {
                        setPurchasedModules(JSON.parse(stored));
                    } catch (parseError) {
                        console.error('Failed to parse stored purchased modules');
                    }
                }
            }
        };

        if (googleResponse?.access_token) {
            fetchPurchasedModules();
        }
    }, [googleResponse, updateGoogleResponse]);

    const login = async (credentials) => {
        // This would typically call the backend API
        // For now, we rely on the mentor-app's auth
        console.log('Login via mentor-app auth');
    };

    const logout = useCallback(() => {
        // Clear all auth data
        clearAuthData();
        setUser(null);
        setPurchasedModules([]);
        updateGoogleResponse(null);

        // Redirect to login
        redirectToLogin();
    }, [updateGoogleResponse]);

    const updatePurchasedModules = (modules) => {
        setPurchasedModules(modules);
        localStorage.setItem('flowstate_purchased_modules', JSON.stringify(modules));
        if (user) {
            const updatedUser = { ...user, purchasedModules: modules };
            setUser(updatedUser);
            localStorage.setItem('flowstate_user', JSON.stringify(updatedUser));
        }
    };

    // Helper to handle API responses with auth check
    const handleAuthError = useCallback((response) => {
        if (response.status === 401) {
            console.log('401 Unauthorized - redirecting to login');
            redirectToLogin();
            return true;
        }
        return false;
    }, []);

    // Create order for Razorpay payment
    const createOrder = async (moduleId) => {
        const accessToken = localStorage.getItem('access_token');

        // Check if not authenticated
        if (!accessToken) {
            redirectToLogin();
            throw new Error('Not authenticated');
        }

        // Check token expiration before API call
        if (isTokenExpired()) {
            redirectToLogin();
            throw new Error('Session expired. Please log in again.');
        }

        // Call backend API to create Razorpay order
        const response = await fetch(`${baseURL}api/interview-prep/payment/order/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ moduleId })
        });

        // Handle 401 Unauthorized
        if (handleAuthError(response)) {
            throw new Error('Session expired. Please log in again.');
        }

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || data.error || 'Failed to create order');
        }
        return data;
    };

    // Verify Razorpay payment
    const verifyPayment = async (paymentData) => {
        const accessToken = localStorage.getItem('access_token');

        // Check if not authenticated
        if (!accessToken) {
            redirectToLogin();
            throw new Error('Not authenticated');
        }

        // Check token expiration before API call
        if (isTokenExpired()) {
            redirectToLogin();
            throw new Error('Session expired. Please log in again.');
        }

        const response = await fetch(`${baseURL}api/interview-prep/payment/verify/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(paymentData)
        });

        // Handle 401 Unauthorized
        if (handleAuthError(response)) {
            throw new Error('Session expired. Please log in again.');
        }

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || data.error || 'Failed to verify payment');
        }

        // Update purchased modules after successful payment
        if (paymentData.moduleId) {
            const newModules = [...purchasedModules, paymentData.moduleId];
            updatePurchasedModules(newModules);
        }

        return data;
    };

    // Check if currently authenticated (token exists and not expired)
    const isAuthenticated = !!(googleResponse?.access_token) && !isTokenExpired();

    const value = {
        user: user ? { ...user, purchasedModules } : null,
        login,
        logout,
        isAuthenticated,
        purchasedModules,
        updatePurchasedModules,
        createOrder,
        verifyPayment
    };

    return (
        <FlowstateAuthContext.Provider value={value}>
            {children}
        </FlowstateAuthContext.Provider>
    );
};

export const useFlowstateAuth = () => {
    const context = useContext(FlowstateAuthContext);
    if (context === undefined) {
        // Return a safe fallback if not in provider
        return {
            user: null,
            login: () => { },
            logout: () => { },
            isAuthenticated: false,
            purchasedModules: [],
            updatePurchasedModules: () => { },
            createOrder: () => Promise.reject(new Error('Not in FlowstateAuthProvider')),
            verifyPayment: () => Promise.reject(new Error('Not in FlowstateAuthProvider'))
        };
    }
    return context;
};

// Alias for backward compatibility
export const useAuth = useFlowstateAuth;

export default FlowstateAuthProvider;
